/* =============================================================================
   Formel lesen: Text -> Baum

   Versteht zwei Schreibweisen gleichzeitig, damit niemand umlernen muss:

   - SCHULE / TASCHENRECHNER   3/4   (x+1)/(x-1)   x^2   x^(n+1)   a_1
                               √16   √(x+1)   sqrt(2)   ³√8   2*3   <=  !=
   - LATEX (z. B. aus ChatGPT) \frac{3}{4}  \sqrt[3]{8}  x^{2}  \cdot  \pi
                               \leq  \neq  \approx  \Rightarrow  \text{cm}

   Knotentypen:
     g        ein Zeichen aus der Schrift           { t, ch, kl }
     sym      ein gezeichnetes Sonderzeichen        { t, name, kl }
     row      eine Folge                            { t, items, kl? }
     bruch    { t, oben, unten }
     wurzel   { t, inhalt, index }
     skript   Hoch-/Tiefstellung { t, basis, hoch, tief }
     klammer  { t, auf, zu, inhalt }
     ueber    Strich oder Pfeil darüber { t, art, inhalt }
     abstand  { t, em }
     text     { t, s }

   kl ist die Rolle für die Abstände: ord (Zahl, Variable), bin (+ − ·),
   rel (= < ≤), punct (, ;), op (sin, log).

   Bruchstrich-Regel für „/“: Zum Zähler und Nenner gehört alles, was direkt
   am Strich steht, bis zum nächsten Rechenzeichen oder Leerzeichen.
   2x/3 ist also (2x)/3, und 1/2 x ist ½·x. Klammern direkt um Zähler oder
   Nenner werden weggelassen, wie man es auch von Hand schreiben würde.
   ========================================================================== */

/* --- Zeichen und Befehle --------------------------------------------------- */

const g = (ch, kl = "ord") => ({ t: "g", ch, kl });
const sym = (name, kl = "ord") => ({ t: "sym", name, kl });

/* Einzelne Zeichen im Formeltext. Alles, was hier fehlt, wird als Zeichen der
   Schrift gesetzt (Buchstaben, Ziffern, %, !, …). */
const ZEICHEN = {
  "+": () => g("+", "bin"),
  "-": () => g("-", "bin"),
  "−": () => g("-", "bin"),
  "–": () => g("-", "bin"),
  "*": () => sym("cdot", "bin"),
  "·": () => sym("cdot", "bin"),
  "⋅": () => sym("cdot", "bin"),
  "×": () => sym("times", "bin"),
  ":": () => g(":", "bin"),
  "÷": () => g(":", "bin"),
  "±": () => sym("pm", "bin"),
  "∓": () => sym("mp", "bin"),
  "∪": () => sym("cup", "bin"),
  "∩": () => sym("cap", "bin"),
  "=": () => g("=", "rel"),
  "<": () => g("<", "rel"),
  ">": () => g(">", "rel"),
  "~": () => g("~", "rel"),
  "≤": () => sym("le", "rel"),
  "≥": () => sym("ge", "rel"),
  "≠": () => sym("ne", "rel"),
  "≈": () => sym("approx", "rel"),
  "≡": () => sym("equiv", "rel"),
  "≅": () => sym("cong", "rel"),
  "→": () => sym("to", "rel"),
  "⇒": () => sym("Rightarrow", "rel"),
  "⇔": () => sym("Leftrightarrow", "rel"),
  "←": () => sym("from", "rel"),
  "⇐": () => sym("Leftarrow", "rel"),
  "↔": () => sym("leftrightarrow", "rel"),
  "∈": () => sym("in", "rel"),
  "∉": () => sym("notin", "rel"),
  "⊂": () => sym("subset", "rel"),
  "⊆": () => sym("subseteq", "rel"),
  "⊥": () => sym("perp", "rel"),
  "∥": () => sym("parallel", "rel"),
  ",": () => g(",", "punct"),
  ";": () => g(";", "punct"),
  "∞": () => sym("infty"),
  "π": () => sym("pi"),
  "∠": () => sym("angle"),
  "∅": () => sym("empty"),
  "°": () => sym("deg"),
  "′": () => sym("prime"),
  "'": () => sym("prime"),
  "µ": () => sym("mu"),
  "Δ": () => sym("Delta"),
  "△": () => sym("Delta"),
  "…": () => ({ t: "row", items: [g("."), g("."), g(".")] }),
  ℝ: () => sym("bbR"),
  ℕ: () => sym("bbN"),
  ℤ: () => sym("bbZ"),
  ℚ: () => sym("bbQ"),
  ℂ: () => sym("bbC"),
};

const GRIECHISCH = {
  α: "alpha", β: "beta", γ: "gamma", δ: "delta", ε: "epsilon", θ: "theta",
  λ: "lambda", μ: "mu", ρ: "rho", σ: "sigma", τ: "tau", φ: "phi", ω: "omega",
  Ω: "Omega", Σ: "Sigma",
};
for (const [ch, name] of Object.entries(GRIECHISCH)) ZEICHEN[ch] = () => sym(name);

/* ASCII-Schreibweisen aus mehreren Zeichen, längste zuerst. */
const MEHRFACH = [
  ["<=>", () => sym("Leftrightarrow", "rel")],
  ["<->", () => sym("leftrightarrow", "rel")],
  ["=/=", () => sym("ne", "rel")],
  ["...", () => ({ t: "row", items: [g("."), g("."), g(".")] })],
  ["<=", () => sym("le", "rel")],
  [">=", () => sym("ge", "rel")],
  ["!=", () => sym("ne", "rel")],
  ["->", () => sym("to", "rel")],
  ["=>", () => sym("Rightarrow", "rel")],
  ["+-", () => sym("pm", "bin")],
  ["~~", () => sym("approx", "rel")],
];

const BEFEHLE = {
  cdot: () => sym("cdot", "bin"),
  times: () => sym("times", "bin"),
  ast: () => sym("cdot", "bin"),
  div: () => g(":", "bin"),
  pm: () => sym("pm", "bin"),
  mp: () => sym("mp", "bin"),
  cup: () => sym("cup", "bin"),
  cap: () => sym("cap", "bin"),
  setminus: () => g("\\", "bin"),
  le: () => sym("le", "rel"),
  leq: () => sym("le", "rel"),
  leqslant: () => sym("le", "rel"),
  ge: () => sym("ge", "rel"),
  geq: () => sym("ge", "rel"),
  geqslant: () => sym("ge", "rel"),
  ne: () => sym("ne", "rel"),
  neq: () => sym("ne", "rel"),
  approx: () => sym("approx", "rel"),
  equiv: () => sym("equiv", "rel"),
  cong: () => sym("cong", "rel"),
  sim: () => g("~", "rel"),
  to: () => sym("to", "rel"),
  rightarrow: () => sym("to", "rel"),
  longrightarrow: () => sym("to", "rel"),
  mapsto: () => sym("to", "rel"),
  Rightarrow: () => sym("Rightarrow", "rel"),
  Longrightarrow: () => sym("Rightarrow", "rel"),
  implies: () => sym("Rightarrow", "rel"),
  Leftrightarrow: () => sym("Leftrightarrow", "rel"),
  Longleftrightarrow: () => sym("Leftrightarrow", "rel"),
  iff: () => sym("Leftrightarrow", "rel"),
  leftarrow: () => sym("from", "rel"),
  Leftarrow: () => sym("Leftarrow", "rel"),
  leftrightarrow: () => sym("leftrightarrow", "rel"),
  in: () => sym("in", "rel"),
  notin: () => sym("notin", "rel"),
  subset: () => sym("subset", "rel"),
  subseteq: () => sym("subseteq", "rel"),
  perp: () => sym("perp", "rel"),
  parallel: () => sym("parallel", "rel"),
  "|": () => sym("parallel", "rel"),
  mid: () => g("|", "rel"),
  vert: () => g("|"),
  lvert: () => g("|"),
  rvert: () => g("|"),
  infty: () => sym("infty"),
  angle: () => sym("angle"),
  measuredangle: () => sym("angle"),
  triangle: () => sym("Delta"),
  emptyset: () => sym("empty"),
  varnothing: () => sym("empty"),
  circ: () => sym("deg"),
  degree: () => sym("deg"),
  prime: () => sym("prime"),
  pi: () => sym("pi"),
  alpha: () => sym("alpha"),
  beta: () => sym("beta"),
  gamma: () => sym("gamma"),
  delta: () => sym("delta"),
  Delta: () => sym("Delta"),
  epsilon: () => sym("epsilon"),
  varepsilon: () => sym("epsilon"),
  theta: () => sym("theta"),
  vartheta: () => sym("theta"),
  lambda: () => sym("lambda"),
  mu: () => sym("mu"),
  rho: () => sym("rho"),
  sigma: () => sym("sigma"),
  tau: () => sym("tau"),
  phi: () => sym("phi"),
  varphi: () => sym("phi"),
  omega: () => sym("omega"),
  Omega: () => sym("Omega"),
  Sigma: () => sym("Sigma"),
  "%": () => g("%"),
  "#": () => g("#"),
  "&": () => g("&"),
  "$": () => g("$"),
  _: () => g("_"),
  "{": () => g("{", "open"),
  "}": () => g("}", "close"),
  lbrace: () => g("{", "open"),
  rbrace: () => g("}", "close"),
  ldots: () => ({ t: "row", items: [g("."), g("."), g(".")] }),
  dots: () => ({ t: "row", items: [g("."), g("."), g(".")] }),
  cdots: () => ({ t: "row", items: [sym("cdot"), sym("cdot"), sym("cdot")] }),
  quad: () => ({ t: "abstand", em: 1 }),
  qquad: () => ({ t: "abstand", em: 2 }),
  ",": () => ({ t: "abstand", em: 0.17 }),
  ":": () => ({ t: "abstand", em: 0.22 }),
  ">": () => ({ t: "abstand", em: 0.22 }),
  ";": () => ({ t: "abstand", em: 0.28 }),
  " ": () => ({ t: "abstand", em: 0.25 }),
  "!": () => ({ t: "abstand", em: -0.1 }),
};

/* Befehle, die einfach verschwinden. */
const IGNORIERT = new Set([
  "displaystyle", "textstyle", "scriptstyle", "limits", "nolimits",
  "big", "Big", "bigg", "Bigg", "bigl", "bigr", "Bigl", "Bigr",
  "biggl", "biggr", "middle", "left", "right", "nonumber", "notag", "label",
]);

/* Funktionsnamen: werden als Wort gesetzt und bekommen danach etwas Luft. */
const FUNKTIONEN = [
  "sin", "cos", "tan", "cot", "arcsin", "arccos", "arctan", "ln", "lg",
  "log", "exp", "min", "max", "lim", "det", "ggT", "kgV", "mod",
];

const HOCH = {
  "⁰": "0", "¹": "1", "²": "2", "³": "3", "⁴": "4", "⁵": "5", "⁶": "6",
  "⁷": "7", "⁸": "8", "⁹": "9", "⁺": "+", "⁻": "-", "⁼": "=", "⁽": "(",
  "⁾": ")", ⁿ: "n",
};
const TIEF = {
  "₀": "0", "₁": "1", "₂": "2", "₃": "3", "₄": "4", "₅": "5", "₆": "6",
  "₇": "7", "₈": "8", "₉": "9", "₊": "+", "₋": "-", "₌": "=", "₍": "(",
  "₎": ")", ₙ: "n", ₓ: "x",
};
const BRUCHZEICHEN = {
  "½": ["1", "2"], "¼": ["1", "4"], "¾": ["3", "4"], "⅓": ["1", "3"],
  "⅔": ["2", "3"], "⅕": ["1", "5"], "⅛": ["1", "8"],
};

const BUCHSTABE = /[A-Za-zÄÖÜäöüßÀ-ÿ]/;

/* --- Zerlegen in Wörter ------------------------------------------------------ */

function zerlege(src) {
  const t = [];
  let i = 0;
  const n = src.length;
  while (i < n) {
    const ch = src[i];

    if (/\s/.test(ch)) {
      while (i < n && /\s/.test(src[i])) i++;
      t.push({ typ: "leer" });
      continue;
    }

    if (ch === "\\") {
      const m = src.slice(i + 1).match(/^([a-zA-Z]+|.)/);
      if (m) {
        t.push({ typ: "befehl", name: m[1] });
        i += 1 + m[1].length;
        // Wie in LaTeX: das Leerzeichen nach \pi beendet nur den Befehl
        if (/^[a-zA-Z]/.test(m[1])) while (i < n && src[i] === " ") i++;
        continue;
      }
      i++;
      continue;
    }

    if (/[0-9]/.test(ch)) {
      let j = i + 1;
      while (j < n && (/[0-9]/.test(src[j]) || (/[.,]/.test(src[j]) && /[0-9]/.test(src[j + 1] || "")))) j++;
      t.push({ typ: "zahl", s: src.slice(i, j) });
      i = j;
      continue;
    }

    if (BUCHSTABE.test(ch)) {
      const rest = src.slice(i);
      const wurzelWort = rest.match(/^(sqrt|wurzel|Wurzel)(?=\s*\()/);
      if (wurzelWort) {
        t.push({ typ: "wurzel" });
        i += wurzelWort[1].length;
        continue;
      }
      if (/^pi(?![A-Za-z])/.test(rest)) {
        t.push({ typ: "atom", knoten: sym("pi") });
        i += 2;
        continue;
      }
      const fn = FUNKTIONEN.find((f) => rest.startsWith(f) && !BUCHSTABE.test(rest[f.length] || "") );
      if (fn) {
        t.push({ typ: "funktion", s: fn });
        i += fn.length;
        continue;
      }
      t.push({ typ: "buchst", ch });
      i++;
      continue;
    }

    if (HOCH[ch] !== undefined) {
      let s = "";
      while (i < n && HOCH[src[i]] !== undefined) s += HOCH[src[i++]];
      if (/^[0-9n]+$/.test(s) && src[i] === "√") {
        t.push({ typ: "wurzel", index: s });
        i++;
        continue;
      }
      t.push({ typ: "uhoch", s });
      continue;
    }
    if (TIEF[ch] !== undefined) {
      let s = "";
      while (i < n && TIEF[src[i]] !== undefined) s += TIEF[src[i++]];
      t.push({ typ: "utief", s });
      continue;
    }
    if (ch === "√") { t.push({ typ: "wurzel" }); i++; continue; }
    if (ch === "∛") { t.push({ typ: "wurzel", index: "3" }); i++; continue; }
    if (ch === "∜") { t.push({ typ: "wurzel", index: "4" }); i++; continue; }
    if (BRUCHZEICHEN[ch]) {
      const [o, u] = BRUCHZEICHEN[ch];
      t.push({ typ: "atom", knoten: { t: "bruch", oben: zahlRow(o), unten: zahlRow(u), kl: "ord" } });
      i++;
      continue;
    }

    const stelle = i;
    const mehr = MEHRFACH.find(([s]) => src.startsWith(s, stelle));
    if (mehr) {
      t.push({ typ: "atom", knoten: mehr[1]() });
      i += mehr[0].length;
      continue;
    }

    if (ch === "^") { t.push({ typ: "hoch" }); i++; continue; }
    if (ch === "_") { t.push({ typ: "tief" }); i++; continue; }
    if (ch === "/") { t.push({ typ: "schraeg" }); i++; continue; }
    if (ch === "(" || ch === "[") { t.push({ typ: "auf", ch }); i++; continue; }
    if (ch === ")" || ch === "]") { t.push({ typ: "zu", ch }); i++; continue; }
    if (ch === "{") { t.push({ typ: "gauf" }); i++; continue; }
    if (ch === "}") { t.push({ typ: "gzu" }); i++; continue; }

    const cp = String.fromCodePoint(src.codePointAt(i));
    t.push({ typ: "zeichen", ch: cp });
    i += cp.length;
  }
  return t;
}

function zahlRow(s) {
  return { t: "row", items: Array.from(s, (c) => (c === "-" || c === "+" ? g(c, "bin") : g(c))) };
}

function zeichenKnoten(ch) {
  if (ZEICHEN[ch]) return ZEICHEN[ch]();
  return g(ch, ch === "!" || ch === "%" ? "ord" : "ord");
}

/* --- Lesen ------------------------------------------------------------------- */

class Leser {
  constructor(tokens) {
    this.t = tokens;
    this.i = 0;
  }
  sieh(k = 0) {
    return this.t[this.i + k];
  }
  nimm() {
    return this.t[this.i++];
  }
  ueberspringeLeer() {
    while (this.sieh() && this.sieh().typ === "leer") this.i++;
  }

  /* Liest bis zu einem Ende (zu, gzu, \right, Textende). Gibt die rohe
     Liste zurück — mit Markierungen für Leerzeichen und Schrägstriche,
     aus denen faltBrueche() die Brüche baut. */
  liesListe(endeBei) {
    const liste = [];
    while (this.i < this.t.length) {
      const tok = this.sieh();
      if (endeBei(tok)) break;
      if (tok.typ === "leer") {
        this.i++;
        liste.push({ t: "_leer" });
        continue;
      }
      if (tok.typ === "schraeg") {
        this.i++;
        liste.push({ t: "_schraeg" });
        continue;
      }
      const item = this.liesElement();
      if (item) liste.push(item);
    }
    return liste;
  }

  liesZeile(endeBei) {
    return zeile(faltBrueche(this.liesListe(endeBei)));
  }

  /* Ein Atom samt angehängter Hoch- und Tiefstellung. */
  liesElement() {
    const basis = this.liesAtom();
    if (!basis) return null;
    let hoch = null;
    let tief = null;
    for (;;) {
      const tok = this.sieh();
      if (!tok) break;
      if (tok.typ === "hoch" && !hoch) {
        this.i++;
        hoch = this.liesSkriptArgument();
      } else if (tok.typ === "tief" && !tief) {
        this.i++;
        tief = this.liesSkriptArgument();
      } else if (tok.typ === "uhoch" && !hoch) {
        this.i++;
        hoch = new Leser(zerlege(tok.s)).liesZeile(() => false);
      } else if (tok.typ === "utief" && !tief) {
        this.i++;
        tief = new Leser(zerlege(tok.s)).liesZeile(() => false);
      } else if (
        tok.typ === "befehl" &&
        (tok.name === "limits" || tok.name === "nolimits")
      ) {
        this.i++;
      } else break;
    }
    if (!hoch && !tief) return basis;
    return { t: "skript", basis, hoch, tief, kl: basis.kl || "ord" };
  }

  /* Was nach ^ oder _ kommt: {…}, (…) ohne die Klammern, eine ganze Zahl,
     ein Buchstabe, ein Vorzeichen mit Atom oder ein Befehl. */
  liesSkriptArgument() {
    this.ueberspringeLeer();
    const tok = this.sieh();
    if (!tok) return { t: "row", items: [] };
    if (tok.typ === "gauf") {
      this.i++;
      const z = this.liesZeile((x) => x.typ === "gzu");
      if (this.sieh() && this.sieh().typ === "gzu") this.i++;
      return z;
    }
    if (tok.typ === "auf" && tok.ch === "(") {
      const k = this.liesKlammer();
      return k.t === "klammer" && k.zu ? k.inhalt : k;
    }
    if (tok.typ === "zahl") {
      this.i++;
      return zahlRow(tok.s);
    }
    if (tok.typ === "zeichen" && (tok.ch === "-" || tok.ch === "+" || tok.ch === "−")) {
      this.i++;
      const danach = this.liesAtom();
      return { t: "row", items: [g("-" === tok.ch || "−" === tok.ch ? "-" : "+", "ord"), danach].filter(Boolean) };
    }
    return this.liesAtom() || { t: "row", items: [] };
  }

  /* Pflicht-Argument eines Befehls: {…} oder ein einzelnes Atom. */
  liesArgument() {
    this.ueberspringeLeer();
    const tok = this.sieh();
    if (!tok) return { t: "row", items: [] };
    if (tok.typ === "gauf") {
      this.i++;
      const z = this.liesZeile((x) => x.typ === "gzu");
      if (this.sieh() && this.sieh().typ === "gzu") this.i++;
      return z;
    }
    if (tok.typ === "zahl") {
      // \frac12 ist ½ — LaTeX nimmt hier nur eine Ziffer
      this.i++;
      if (tok.s.length > 1) {
        this.t.splice(this.i, 0, { typ: "zahl", s: tok.s.slice(1) });
      }
      return g(tok.s[0]);
    }
    return this.liesAtom() || { t: "row", items: [] };
  }

  /* Rohtext in {…} für \text: Leerzeichen bleiben erhalten. */
  liesRohText() {
    this.ueberspringeLeer();
    const tok = this.sieh();
    if (!tok || tok.typ !== "gauf") return "";
    this.i++;
    let s = "";
    let tiefe = 1;
    while (this.i < this.t.length) {
      const x = this.nimm();
      if (x.typ === "gauf") tiefe++;
      if (x.typ === "gzu" && --tiefe === 0) break;
      s += tokenAlsText(x);
    }
    return s;
  }

  liesKlammer() {
    const auf = this.nimm(); // { typ: "auf", ch }
    const start = this.i;
    const liste = this.liesListe((x) => x.typ === "zu");
    const zu = this.sieh();
    if (!zu) {
      // Keine schließende Klammer: die öffnende ist ein einfaches Zeichen.
      this.i = start;
      return g(auf.ch, "open");
    }
    this.i++;
    return { t: "klammer", auf: auf.ch, zu: zu.ch, inhalt: zeile(faltBrueche(liste)), kl: "ord" };
  }

  liesAtom() {
    const tok = this.sieh();
    if (!tok) return null;

    switch (tok.typ) {
      case "zahl":
        this.i++;
        return zahlRow(tok.s);
      case "buchst":
        this.i++;
        return g(tok.ch);
      case "funktion":
        this.i++;
        return { t: "row", items: Array.from(tok.s, (c) => g(c)), kl: "op" };
      case "atom":
        this.i++;
        return tok.knoten;
      case "zeichen":
        this.i++;
        return zeichenKnoten(tok.ch);
      case "auf":
        return this.liesKlammer();
      case "zu":
        this.i++;
        return g(tok.ch, "close");
      case "gauf": {
        this.i++;
        const z = this.liesZeile((x) => x.typ === "gzu");
        if (this.sieh() && this.sieh().typ === "gzu") this.i++;
        return z;
      }
      case "gzu":
        this.i++;
        return null;
      case "wurzel": {
        this.i++;
        this.ueberspringeLeer();
        const naechstes = this.sieh();
        let inhalt;
        if (naechstes && naechstes.typ === "auf" && naechstes.ch === "(") {
          const k = this.liesKlammer();
          inhalt = k.t === "klammer" ? k.inhalt : k;
        } else {
          inhalt = this.liesElement() || { t: "row", items: [] };
        }
        const index = tok.index ? zahlRow(tok.index) : null;
        return { t: "wurzel", inhalt, index, kl: "ord" };
      }
      case "hoch":
      case "tief":
        // Hochstellung ohne Basis, etwa ^2 am Anfang: leere Basis
        return { t: "row", items: [] };
      case "uhoch": {
        this.i++;
        const hoch = new Leser(zerlege(tok.s)).liesZeile(() => false);
        return { t: "skript", basis: { t: "row", items: [] }, hoch, tief: null, kl: "ord" };
      }
      case "utief": {
        this.i++;
        const tief = new Leser(zerlege(tok.s)).liesZeile(() => false);
        return { t: "skript", basis: { t: "row", items: [] }, hoch: null, tief, kl: "ord" };
      }
      case "befehl":
        this.i++;
        return this.befehl(tok.name);
      default:
        this.i++;
        return null;
    }
  }

  befehl(name) {
    if (name === "frac" || name === "dfrac" || name === "tfrac" || name === "cfrac") {
      const oben = this.liesArgument();
      const unten = this.liesArgument();
      return { t: "bruch", oben, unten, kl: "ord" };
    }
    if (name === "sqrt") {
      this.ueberspringeLeer();
      let index = null;
      if (this.sieh() && this.sieh().typ === "auf" && this.sieh().ch === "[") {
        this.i++;
        index = this.liesZeile((x) => x.typ === "zu");
        if (this.sieh()) this.i++;
      }
      const inhalt = this.liesArgument();
      return { t: "wurzel", inhalt, index, kl: "ord" };
    }
    if (name === "left") {
      return this.liesLeftRight();
    }
    if (name === "right") {
      // ein \right ohne \left: Begrenzer verschlucken
      this.ueberspringeLeer();
      const d = this.sieh();
      if (d) this.i++;
      return null;
    }
    if (["overline", "bar", "widebar"].includes(name)) {
      return { t: "ueber", art: "linie", inhalt: this.liesArgument(), kl: "ord" };
    }
    if (["vec", "overrightarrow"].includes(name)) {
      return { t: "ueber", art: "pfeil", inhalt: this.liesArgument(), kl: "ord" };
    }
    if (["text", "textrm", "mathrm", "textit", "mathit", "textbf", "mathbf", "operatorname", "mbox", "textnormal", "mathsf", "textsf"].includes(name)) {
      return { t: "text", s: this.liesRohText(), kl: name === "operatorname" ? "op" : "ord" };
    }
    if (name === "mathbb" || name === "mathbf" || name === "Bbb") {
      const s = this.liesRohText();
      return sym("bb" + (s.trim()[0] || "R"));
    }
    if (["hat", "widehat", "tilde", "underline", "boxed", "mathcal", "pmb", "dot", "ddot"].includes(name)) {
      return this.liesArgument();
    }
    if (name === "binom") {
      const oben = this.liesArgument();
      const unten = this.liesArgument();
      return { t: "klammer", auf: "(", zu: ")", inhalt: { t: "bruch", oben, unten, ohneStrich: true }, kl: "ord" };
    }
    if (FUNKTIONEN.includes(name)) {
      return { t: "row", items: Array.from(name, (c) => g(c)), kl: "op" };
    }
    if (IGNORIERT.has(name)) return null;
    if (BEFEHLE[name]) return BEFEHLE[name]();
    // Unbekannter Befehl: den Namen hinschreiben ist besser als nichts.
    if (/^[a-zA-Z]+$/.test(name)) return { t: "text", s: name, kl: "ord" };
    return g(name);
  }

  /* \left( … \right) — die Klammern wachsen mit dem Inhalt. */
  liesLeftRight() {
    this.ueberspringeLeer();
    const begrenzer = (tok) => {
      if (!tok) return "";
      if (tok.typ === "auf" || tok.typ === "zu") return tok.ch;
      if (tok.typ === "zeichen") return tok.ch === "." ? "" : tok.ch;
      if (tok.typ === "befehl") {
        if (tok.name === "{" || tok.name === "lbrace") return "{";
        if (tok.name === "}" || tok.name === "rbrace") return "}";
        if (tok.name === "|") return "‖";
        if (tok.name === "vert" || tok.name === "lvert" || tok.name === "rvert") return "|";
        if (tok.name === "langle") return "⟨";
        if (tok.name === "rangle") return "⟩";
      }
      return "";
    };
    const aufTok = this.nimm();
    const auf = begrenzer(aufTok);
    const liste = this.liesListe((x) => x.typ === "befehl" && x.name === "right");
    let zu = "";
    if (this.sieh()) {
      this.i++; // \right
      this.ueberspringeLeer();
      zu = begrenzer(this.nimm());
    }
    return { t: "klammer", auf, zu, inhalt: zeile(faltBrueche(liste)), kl: "ord", wachsen: true };
  }
}

function tokenAlsText(x) {
  switch (x.typ) {
    case "leer": return " ";
    case "zahl": return x.s;
    case "buchst": return x.ch;
    case "funktion": return x.s;
    case "zeichen": return x.ch;
    case "auf":
    case "zu": return x.ch;
    case "schraeg": return "/";
    case "hoch": return "^";
    case "tief": return "_";
    case "befehl": return x.name.length === 1 ? x.name : "";
    case "atom": return x.knoten && x.knoten.t === "sym" && x.knoten.name === "pi" ? "pi" : "";
    default: return "";
  }
}

const istOperand = (x) => x && x.t !== "_leer" && x.t !== "_schraeg" && !["bin", "rel", "punct"].includes(x.kl);

function zeile(items) {
  // Ein Leerzeichen zwischen zwei Zahlen oder Variablen (gemischte Zahl
  // 2 3/4, 2 x) bleibt als kleine Lücke stehen; sonst zählen Leerzeichen
  // nicht, die Abstände um + und = kommen aus layout.js.
  const mitLuecken = items.map((x, i) =>
    x && x.t === "_leer" && istOperand(items[i - 1]) && istOperand(items[i + 1])
      ? { t: "abstand", em: 0.16 }
      : x
  );
  // Verschachtelte schlichte Zeilen (Zahlen, {…}) flach machen
  const sauber = mitLuecken
    .filter((x) => x && x.t !== "_leer" && x.t !== "_schraeg")
    .flatMap((x) => (x.t === "row" && !x.kl ? x.items : [x]));
  if (sauber.length === 1) return sauber[0];
  return { t: "row", items: sauber };
}

const istGrenze = (x) =>
  !x || x.t === "_leer" || x.t === "_schraeg" || x.kl === "bin" || x.kl === "rel" || x.kl === "punct";

const istVorzeichen = (x) => x && x.t === "g" && (x.ch === "-" || x.ch === "+");

function operand(liste) {
  if (liste.length === 1) {
    const x = liste[0];
    if (x.t === "klammer" && x.auf === "(" && x.zu === ")") return x.inhalt;
    return x;
  }
  return zeile(liste);
}

/* Macht aus „a / b“ in der rohen Liste einen Bruch (siehe Kopf der Datei). */
function faltBrueche(liste) {
  if (!liste.some((x) => x.t === "_schraeg")) return liste;
  const aus = [];
  for (let i = 0; i < liste.length; i++) {
    const x = liste[i];
    if (x.t !== "_schraeg") {
      aus.push(x);
      continue;
    }
    // Leerzeichen direkt am Strich zählen nicht: a / b ist auch ein Bruch
    while (aus.length && aus[aus.length - 1].t === "_leer") aus.pop();
    const links = [];
    while (aus.length && !istGrenze(aus[aus.length - 1])) links.unshift(aus.pop());

    let j = i + 1;
    while (j < liste.length && liste[j].t === "_leer") j++;
    const rechts = [];
    if (j < liste.length && istVorzeichen(liste[j])) rechts.push({ ...liste[j++], kl: "ord" });
    while (j < liste.length && !istGrenze(liste[j])) rechts.push(liste[j++]);

    if (!links.length || !rechts.length || (rechts.length === 1 && istVorzeichen(rechts[0]))) {
      // Kein vollständiger Bruch — dann eben ein Schrägstrich.
      aus.push(...links, g("/"), ...rechts);
      i = j - 1;
      continue;
    }
    aus.push({ t: "bruch", oben: operand(links), unten: operand(rechts), kl: "ord" });
    i = j - 1;
  }
  return aus;
}

/* --- Einstieg ------------------------------------------------------------- */

const merker = new Map();

export function leseFormel(src) {
  if (merker.has(src)) return merker.get(src);
  let baum;
  try {
    const leser = new Leser(zerlege(src));
    const teile = [];
    // Überzählige } oder ) am Ende dürfen das Lesen nicht abbrechen
    while (leser.i < leser.t.length) {
      teile.push(...leser.liesListe(() => false));
      if (leser.i < leser.t.length) leser.i++;
    }
    baum = zeile(faltBrueche(teile));
  } catch (e) {
    baum = { t: "text", s: src };
  }
  if (merker.size > 500) merker.clear();
  merker.set(src, baum);
  return baum;
}
